// src/services/transactionService.js
// All Supabase operations for transactions + balance updates

import { supabase } from '../lib/supabase'
import { updateAccountBalance, getAccount } from './accountService'

/**
 * Fetch all transactions for the current user
 */
export async function fetchTransactions() {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .order('date', { ascending: false })
    .order('created_at', { ascending: false })

  if (error) return { data: [], error: error.message }

  // Normalise column names to match our frontend shape
  return { data: data.map(normalise), error: null }
}

/**
 * Create a transaction and update account balance(s)
 */
export async function createTransaction(txnData) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { data: null, error: 'Not authenticated' }

  // Build DB row
  const row = buildRow(txnData, user.id)

  const { data, error } = await supabase
    .from('transactions')
    .insert(row)
    .select()
    .single()

  if (error) return { data: null, error: error.message }

  // Update balances
  await applyBalanceChange(txnData, +1)

  return { data: normalise(data), error: null }
}

/**
 * Update a transaction — reverses old, applies new balance change
 */
export async function updateTransaction(id, oldTxn, newTxnData) {
  // Reverse the old transaction's balance effect
  await applyBalanceChange(oldTxn, -1)

  const row = buildRow(newTxnData)
  delete row.user_id  // don't overwrite owner

  const { data, error } = await supabase
    .from('transactions')
    .update(row)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    // Re-apply old to avoid leaving balances wrong
    await applyBalanceChange(oldTxn, +1)
    return { data: null, error: error.message }
  }

  // Apply new transaction's balance effect
  await applyBalanceChange(newTxnData, +1)

  return { data: normalise(data), error: null }
}

/**
 * Delete a transaction and reverse its balance effect
 */
export async function deleteTransaction(id, txnData) {
  const { error } = await supabase
    .from('transactions')
    .delete()
    .eq('id', id)

  if (error) return { error: error.message }

  // Reverse balance impact
  await applyBalanceChange(txnData, -1)

  return { error: null }
}

/**
 * Generate next TXN reference number for a user
 */
export async function getNextRef() {
  const { count } = await supabase
    .from('transactions')
    .select('*', { count: 'exact', head: true })

  const next = (count || 0) + 1
  return 'TXN-' + String(next).padStart(3, '0')
}

// ── Private helpers ─────────────────────────────────────────

/**
 * Build DB row from frontend transaction shape
 */
function buildRow(txn, userId) {
  const row = {
    ref:        txn.ref,
    date:       txn.date,
    type:       txn.type,
    cat:        txn.cat || 'Transfer',
    amt:        parseFloat(txn.amt),
    note:       txn.note || '',
    acct:       null,
    from_acct:  null,
    to_acct:    null,
  }
  if (userId) row.user_id = userId

  if (txn.type === 'transfer') {
    row.from_acct = txn.from
    row.to_acct   = txn.to
  } else {
    row.acct = txn.acct
  }

  return row
}

/**
 * Convert DB row back to frontend shape
 */
function normalise(row) {
  return {
    id:   row.id,
    ref:  row.ref,
    date: row.date,
    type: row.type,
    cat:  row.cat,
    amt:  parseFloat(row.amt),
    note: row.note || '',
    acct:      row.acct      || null,
    from:      row.from_acct || null,
    to:        row.to_acct   || null,
    createdAt: row.created_at,
  }
}

/**
 * Apply balance change to account(s) based on transaction type
 * sign = +1 to apply, -1 to reverse
 */
async function applyBalanceChange(txn, sign) {
  const amt = parseFloat(txn.amt) * sign

  if (txn.type === 'income') {
    await adjustBalance(txn.acct, +amt)
  } else if (txn.type === 'expense') {
    await adjustBalance(txn.acct, -amt)
  } else if (txn.type === 'transfer') {
    await adjustBalance(txn.from, -amt)
    await adjustBalance(txn.to,   +amt)
  }
}

/**
 * Fetch current balance, add delta, write back
 */
async function adjustBalance(acctId, delta) {
  if (!acctId) return
  const { data: acct } = await getAccount(acctId)
  if (!acct) return
  const newBalance = parseFloat((acct.balance + delta).toFixed(2))
  await updateAccountBalance(acctId, newBalance)
}
